import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Search, Edit2, Trash2, X, Save, AlertCircle, ThumbsUp, ThumbsDown, AlertTriangle } from 'lucide-react';
import './ImproveAgent.css';

const ImproveAgent = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [feedback, setFeedback] = useState([]);
  const [filteredFeedback, setFilteredFeedback] = useState([]);
  const [paginatedFeedback, setPaginatedFeedback] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});
  const [expandedContext, setExpandedContext] = useState({});
  const [error, setError] = useState(null);

  // Load feedback data
  useEffect(() => {
    console.log('🔍 [FRONTEND] useEffect triggered');
    console.log('🔍 [FRONTEND] user:', user);
    console.log('🔍 [FRONTEND] user.agent:', user?.agent);
    console.log('🔍 [FRONTEND] user.agent.id:', user?.agent?.id);
    
    if (user?.agent?.id) {
      console.log('✅ [FRONTEND] User and agent found, calling loadFeedback');
      loadFeedback();
    } else {
      console.log('❌ [FRONTEND] User or agent not found, not calling loadFeedback');
    }
  }, [user?.agent?.id]);

  // Filter feedback
  useEffect(() => {
    let filtered = feedback;
    
    // Apply search filter
    if (searchTerm.trim() !== '') {
      filtered = feedback.filter(item => {
        const searchLower = searchTerm.toLowerCase();
        const matchesBasic = 
          item.user_question.toLowerCase().includes(searchLower) ||
          item.original_response.toLowerCase().includes(searchLower) ||
          (item.corrected_response && item.corrected_response.toLowerCase().includes(searchLower));
        
        // Also search in context data if available
        let matchesContext = false;
        if (item.context_summary) {
          try {
            const contextData = JSON.parse(item.context_summary);
            const contextString = JSON.stringify(contextData).toLowerCase();
            matchesContext = contextString.includes(searchLower);
          } catch (error) {
            // If context parsing fails, just skip context search
          }
        }
        
        return matchesBasic || matchesContext;
      });
    }
    
    // Sort by date (newest first) - simple and effective
    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    setFilteredFeedback(filtered);
    
    // Reset to first page when filtering changes
    setCurrentPage(1);
  }, [searchTerm, feedback]);

  // Pagination effect
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = filteredFeedback.slice(startIndex, endIndex);
    setPaginatedFeedback(paginated);
  }, [filteredFeedback, currentPage, itemsPerPage]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Escape key to close modals
      if (e.key === 'Escape') {
        if (editingId) {
          setEditingId(null);
          setEditText('');
          setError(null);
        } else if (showDeleteConfirm) {
          setShowDeleteConfirm(null);
        }
      }
      
      // Ctrl/Cmd + F to focus search
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        const searchInput = document.querySelector('.search-box input');
        if (searchInput) {
          searchInput.focus();
        }
      }
      
      // Ctrl/Cmd + E to start editing (if a card is selected)
      if ((e.ctrlKey || e.metaKey) && e.key === 'e' && !editingId) {
        e.preventDefault();
        // This would need more complex logic to determine which card to edit
        // For now, we'll just focus the first editable card
        const firstEditableCard = document.querySelector('.feedback-card');
        if (firstEditableCard) {
          const editButton = firstEditableCard.querySelector('.edit-btn');
          if (editButton) {
            editButton.click();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingId, showDeleteConfirm]);

  const loadFeedback = async () => {
    try {
      console.log('🔍 [FRONTEND] loadFeedback called');
      console.log('🔍 [FRONTEND] user:', user);
      console.log('🔍 [FRONTEND] user.agent:', user?.agent);
      console.log('🔍 [FRONTEND] user.agent.id:', user?.agent?.id);
      console.log('🔍 [FRONTEND] token:', localStorage.getItem('token'));
      
      setLoading(true);
      setError(null);
      
      const url = `/api/agents/feedback/${user.agent.id}`;
      console.log('🔍 [FRONTEND] Making request to:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      console.log('🔍 [FRONTEND] Response status:', response.status);
      console.log('🔍 [FRONTEND] Response ok:', response.ok);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ [FRONTEND] Successfully loaded feedback:', data);
        setFeedback(data);
        setFilteredFeedback(data);
      } else {
        const errorText = await response.text();
        console.error('❌ [FRONTEND] Response error:', errorText);
        throw new Error(`Failed to load feedback: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('❌ [FRONTEND] Error loading feedback:', error);
      setError('Failed to load feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditText(item.corrected_response || '');
  };

  const handleSaveEdit = async () => {
    if (!editText.trim()) {
      setError('Please enter a corrected response.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const response = await fetch(`/api/agents/feedback/${editingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          correctedResponse: editText.trim(),
          rating: feedback.find(f => f.id === editingId)?.rating || -1,
          feedbackType: 'correction',
          agentId: user.agent.id
        })
      });

      if (response.ok) {
        // Update local state
        setFeedback(prev => prev.map(item => 
          item.id === editingId 
            ? { ...item, corrected_response: editText.trim() }
            : item
        ));
        setFilteredFeedback(prev => prev.map(item => 
          item.id === editingId 
            ? { ...item, corrected_response: editText.trim() }
            : item
        ));
        setEditingId(null);
        setEditText('');
      } else {
        throw new Error(`Failed to update feedback: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error updating feedback:', error);
      setError('Failed to update feedback. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (feedbackId) => {
    try {
      console.log('Attempting to delete feedback with ID:', feedbackId);
      setDeleting(true);
      setError(null);
      const response = await fetch(`/api/agents/feedback/${feedbackId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          agentId: user.agent.id
        })
      });
      
      console.log('Delete response status:', response.status);

      if (response.ok) {
        // Update local state
        setFeedback(prev => prev.filter(item => item.id !== feedbackId));
        setFilteredFeedback(prev => prev.filter(item => item.id !== feedbackId));
        setShowDeleteConfirm(null);
        setEditingId(null);
        setEditText('');
      } else {
        throw new Error(`Failed to delete feedback: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting feedback:', error);
      setError('Failed to delete feedback. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const highlightText = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="search-highlight">{part}</mark>
      ) : part
    );
  };


  const toggleCardExpansion = (cardId) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  const toggleContextExpansion = (cardId) => {
    setExpandedContext(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  if (loading) {
    return (
      <div className="improve-agent">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading feedback...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="improve-agent" data-theme="dark">
      {error && (
        <div className="error-banner">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}

      {feedback && feedback.length === 0 ? (
        <div className="empty-state">
          <AlertCircle size={48} />
          <h3>No feedback yet</h3>
          <p>When you provide corrections or thumbs up/down to your agent's responses, they will appear here for you to review and edit.</p>
        </div>
      ) : (
        <>
          <div className="search-container">
            <div className="search-box">
              <Search size={20} />
              <input
                type="text"
                placeholder="Search feedback..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="feedback-list">
            {paginatedFeedback && paginatedFeedback.map((item) => {
              const isExpanded = expandedCards[item.id];
              const isPositive = item.rating > 0;
              const isNegative = item.rating < 0;
              const isCorrection = item.feedback_type === 'correction';
              
              return (
                <div 
                  key={item.id} 
                  className={`feedback-card ${isPositive ? 'positive' : isNegative ? 'negative' : ''} ${isCorrection ? 'correction' : ''}`}
                >
                  {/* Feedback Type Indicator */}
                  <div className="feedback-type-indicator">
                    {isCorrection ? (
                      <div className="type-badge correction">
                        <AlertTriangle size={14} />
                        <span>Correction Given</span>
                      </div>
                    ) : isPositive ? (
                      <div className="type-badge positive">
                        <ThumbsUp size={14} />
                        <span>Positive Feedback</span>
                      </div>
                    ) : isNegative ? (
                      <div className="type-badge negative">
                        <ThumbsDown size={14} />
                        <span>Negative Feedback</span>
                      </div>
                    ) : null}
                  </div>

                  <div className="feedback-content">
                    <div className="feedback-section">
                      <div className="section-label">Question</div>
                      <div className="section-content">{highlightText(item.user_question, searchTerm)}</div>
                    </div>

                    {/* Show response - single box for thumbs up, two boxes for corrections */}
                    {isCorrection ? (
                      <>
                        <div className="feedback-section">
                          <div className="section-label">Original Response</div>
                          <div className="section-content">
                            {isExpanded ? highlightText(item.original_response || '', searchTerm) : highlightText(truncateText(item.original_response || ''), searchTerm)}
                            {item.original_response && item.original_response.length > 150 && (
                              <button 
                                className="show-more-btn"
                                onClick={() => toggleCardExpansion(item.id)}
                              >
                                {isExpanded ? 'Show less' : 'Show more'}
                              </button>
                            )}
                          </div>
                        </div>

                        {item.corrected_response && (
                          <div className="feedback-section correction">
                            <div className="section-label">Corrected Response</div>
                            <div className="section-content">
                              {isExpanded ? highlightText(item.corrected_response || '', searchTerm) : highlightText(truncateText(item.corrected_response || ''), searchTerm)}
                              {item.corrected_response && item.corrected_response.length > 150 && (
                                <button 
                                  className="show-more-btn"
                                  onClick={() => toggleCardExpansion(item.id)}
                                >
                                  {isExpanded ? 'Show less' : 'Show more'}
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="feedback-section">
                        <div className="section-label">Response</div>
                        <div className="section-content">
                          {isExpanded ? highlightText(item.original_response || '', searchTerm) : highlightText(truncateText(item.original_response || ''), searchTerm)}
                          {item.original_response && item.original_response.length > 150 && (
                            <button 
                              className="show-more-btn"
                              onClick={() => toggleCardExpansion(item.id)}
                            >
                              {isExpanded ? 'Show less' : 'Show more'}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Context Data Section */}
                    {item.context_summary && (
                      <div className="context-section">
                        <button 
                          className="context-toggle-btn"
                          onClick={() => toggleContextExpansion(item.id)}
                        >
                          <Search size={16} />
                          Context Data ({item.context_summary ? Object.keys(JSON.parse(item.context_summary)).length : 0} items)
                        </button>
                        {expandedContext[item.id] && item.context_summary && (
                          <div className="context-content">
                            {Object.entries(JSON.parse(item.context_summary)).map(([category, data]) => (
                              <div key={category} className="context-category">
                                <h5>{category}:</h5>
                                {typeof data === 'object' && data !== null ? (
                                  <ul>
                                    {Object.entries(data).map(([key, value]) => (
                                      <li key={key}><strong>{key}:</strong> {String(value)}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p>{String(data)}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Actions Row - Edit and See in context buttons */}
                    <div className="feedback-actions-row">
                      <button 
                        className="edit-btn"
                        onClick={() => handleEdit(item)}
                        title="Edit correction"
                      >
                        <Edit2 size={16} />
                        Edit
                      </button>
                      <button 
                        className="context-btn"
                        onClick={() => {
                          // Navigate to chat section first
                          navigate('/build');
                          
                          // Then load the conversation
                          setTimeout(() => {
                            const event = new CustomEvent('loadConversation', { 
                              detail: { conversationId: item.conversation_id } 
                            });
                            window.dispatchEvent(event);
                          }, 100);
                        }}
                        title="View this conversation"
                      >
                        See in context
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {filteredFeedback && filteredFeedback.length > itemsPerPage && (
            <div className="pagination">
              <div className="pagination-info">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredFeedback.length)} of {filteredFeedback.length} feedback items
              </div>
              <div className="pagination-controls">
                <button 
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>
                <div className="pagination-pages">
                  {Array.from({ length: Math.ceil(filteredFeedback.length / itemsPerPage) }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`pagination-page ${currentPage === page ? 'active' : ''}`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button 
                  onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredFeedback.length / itemsPerPage), prev + 1))}
                  disabled={currentPage === Math.ceil(filteredFeedback.length / itemsPerPage)}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Edit Modal */}
      {editingId && (
        <div className="modal-overlay">
          <div className="edit-modal">
            <div className="modal-header">
              <h3>Edit Correction</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  setEditingId(null);
                  setEditText('');
                }}
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              <div className="modal-question">
                <label>Question:</label>
                <p>{feedback.find(f => f.id === editingId)?.user_question}</p>
              </div>
              <div className="modal-response">
                <label>Corrected Response:</label>
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  placeholder="Enter the corrected response..."
                  rows={6}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => {
                  setEditingId(null);
                  setEditText('');
                  setError(null);
                }}
                disabled={saving}
              >
                Cancel
              </button>
              <button 
                className="delete-btn"
                onClick={() => setShowDeleteConfirm(editingId)}
                disabled={saving || deleting}
                title="Delete feedback"
              >
                {deleting ? (
                  <>
                    <div className="button-spinner"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Delete
                  </>
                )}
              </button>
              <button 
                className="save-btn"
                onClick={handleSaveEdit}
                disabled={saving || !editText.trim()}
              >
                {saving ? (
                  <>
                    <div className="button-spinner"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="delete-modal">
            <div className="modal-header">
              <h3>Delete Feedback</h3>
              <button 
                className="close-btn"
                onClick={() => setShowDeleteConfirm(null)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              <p>Are you sure you want to delete this feedback? This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => setShowDeleteConfirm(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                className="delete-btn"
                onClick={() => handleDelete(showDeleteConfirm)}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <div className="button-spinner"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImproveAgent;
